import React, { useState, useEffect } from "react";
import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Label,
  Field,
  Radio,
  RadioGroup,
} from "@headlessui/react";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { ComponentI } from "../../../../types";
import Card from "../../../../componentLibrary/Card";
import Checkbox from "../../../../componentLibrary/Checkbox";
import { ImageI, ImageTypeEnum } from "../../../../types";
import { useGetImages, useGetImageById } from "../../../../api/queries";
import Spinner from "../../../../componentLibrary/Spinner";
import Tooltip from "../../../../componentLibrary/Tooltip";
import AddImageModal from "../../../../components/AddImageModal";
import Button from "../../../../componentLibrary/Button";

export default function ImageContentCard({
  component,
  handleContentChange,
  provided,
}: {
  component: ComponentI;
  handleContentChange: (
    id: string,
    field: string,
    value: string | number | boolean,
  ) => void;
  provided: any; //eslint-disable-line @typescript-eslint/no-explicit-any
}) {
  const [imageType, setImageType] = useState<ImageTypeEnum | "All">("All");
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [query, setQuery] = useState("");
  const [filteredIcons, setFilteredIcons] = useState([]);
  const {
    data: images,
    isError,
    isLoading,
  } = useGetImages(imageType === "All" ? undefined : imageType);
  const {
    data: pickedImageDetail,
    isError: isErrorImage,
    isLoading: isLoadingImage,
  } = useGetImageById(component.content?.imageId || "");

  const changeImageType = (option) => {
    setImageType(option);
    handleContentChange(component._id, "imageId", null);
  };

  useEffect(() => {
    if (images) {
      const filteredIcons1 =
        query === ""
          ? images
          : images.filter((icon) =>
              icon.name.toLowerCase().includes(query.toLowerCase()),
            );
      setFilteredIcons(filteredIcons1);
    }
  }, [images, query]);

  const setSelectedOption = (option) => {
    handleContentChange(component._id, "imageId", option);
  };

  const getImageFromId = (id: string) => {
    const selectedImage = images.find((image) => image._id === id);
    if (selectedImage) {
      return selectedImage;
    }
    return null;
  };

  if (isLoading || isLoadingImage) return <Spinner className="mx-auto" />;
  if (isError || !images || isErrorImage) return <div>Error</div>;

  return (
    <Card>
      <div className="space-y-2">
        <div className="flex flex-row justify-between items-center">
          <div
            className="w-2/3 relative"
            data-rbd-drag-handle-context-id={
              provided.dragHandleProps?.["data-rbd-drag-handle-context-id"]
            }
            data-rbd-drag-handle-draggable-id="gibberish"
          >
            <Combobox
              immediate
              value={getImageFromId(component.content?.imageId)}
              onChange={setSelectedOption}
            >
              <Label className="block text-sm font-medium text-gray-700">
                {""}
              </Label>
              <ComboboxInput
                aria-label="Icon"
                displayValue={(icon: ImageI | null) => icon?.name || ""}
                onChange={(event) => setQuery(event.target.value)}
                autoComplete="off"
                placeholder="Pick from Existing Images"
                className="w-full px-4 py-2 border border-[#0F172A] rounded-md focus:outline-none focus:border-[#00A3E0] disabled:cursor-not-allowed"
                disabled={false}
              />
              <ComboboxOptions className="absolute mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm z-40">
                {filteredIcons.length === 0 && query !== "" ? (
                  <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                    No results found
                  </div>
                ) : (
                  filteredIcons.map((option) => (
                    <ComboboxOption
                      key={option._id}
                      value={option._id}
                      className={({ active }) =>
                        `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? "bg-blue-100" : ""}`
                      }
                    >
                      {({ selected }) => (
                        <div className="flex items-center">
                          <img
                            src={option.src}
                            alt={option.alt}
                            className="h-6 w-6 flex-shrink-0"
                          />
                          <span
                            className={`ml-3 block truncate ${selected ? "font-medium" : "font-normal"}`}
                          >
                            {option.name}
                          </span>
                        </div>
                      )}
                    </ComboboxOption>
                  ))
                )}
              </ComboboxOptions>
            </Combobox>
          </div>
          {pickedImageDetail?.src ? (
            <img
              src={pickedImageDetail?.src}
              alt={pickedImageDetail?.alt}
              className={`h-16 w-16 bg-gray-100 border-2 border-dashed ${component.content.hasBorder ? "border-[#00A3E0] rounded-md" : ""}`}
            />
          ) : (
            <div className="h-16 w-16 bg-gray-100 border-2 border-dashed"></div>
          )}
        </div>
        <div className="flex flex-row justify-between content-center">
          <RadioGroup
            value={imageType}
            onChange={changeImageType}
            aria-label="Server size"
            className={"flex flex-row justify-between gap-6"}
          >
            <Field key="all" className="flex items-center gap-2">
              <Radio
                value={"All"}
                className="group flex size-6 items-center justify-center rounded-full bg-electrum-100 data-[checked]:bg-electrum-400"
              ></Radio>
              <Label>All</Label>
            </Field>
            {Object.keys(ImageTypeEnum).map((imageType, index) => (
              <Field key={index} className="flex items-center gap-2">
                <Radio
                  value={ImageTypeEnum[imageType]}
                  className="group flex size-6 items-center justify-center rounded-full bg-electrum-100 data-[checked]:bg-electrum-400"
                ></Radio>
                <Label>{ImageTypeEnum[imageType]}</Label>
              </Field>
            ))}
          </RadioGroup>
          {imageType !== "All" && (
            <Button onClick={() => setIsModalOpen(true)}>
              Upload {imageType}
            </Button>
          )}
        </div>
        <div
          data-rbd-drag-handle-context-id={
            provided.dragHandleProps?.["data-rbd-drag-handle-context-id"]
          }
          data-rbd-drag-handle-draggable-id="gibberish"
        >
          <div className="flex flex-row space-x-1 items-center">
            <div className="font-bold">Width</div>
            <Tooltip
              icon={<InformationCircleIcon className="h-4 w-4 text-gray-600" />}
              text="Adjust the width of the image as a percentage of its container while maintaining its aspect ratio."
              position="right"
              isActive={true}
            />
          </div>
          <RadioGroup
            value={component.content.width || 100}
            onChange={(value) =>
              handleContentChange(component._id, "width", value)
            }
            className="flex flex-row justify-between gap-2 mt-2"
          >
            {[20, 40, 60, 80, 100].map((width) => (
              <Field key={width} className="flex items-center gap-2">
                <Radio
                  value={width}
                  className="group flex size-6 items-center justify-center rounded-full bg-electrum-100 data-[checked]:bg-electrum-400"
                ></Radio>
                <Label>{width}</Label>
              </Field>
            ))}
          </RadioGroup>
        </div>

        <div className="flex items-center space-x-2 mt-2">
          <Checkbox
            label="Add border with rounded edges"
            checked={component.content.hasBorder || false}
            onChange={(e) => {
              const value: string | number | boolean = e.target.checked;
              handleContentChange(component._id, "hasBorder", value);
            }}
          />
          <Tooltip
            icon={<InformationCircleIcon className="h-4 w-4 text-gray-600" />}
            text="Apply a border with rounded corners to the image"
            position="right"
            isActive={true}
          />
        </div>
      </div>
      {imageType !== "All" && (
        <AddImageModal
          type={imageType}
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
        />
      )}
    </Card>
  );
}
